import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { normalizeDateOfBirth } from '../lib/passengerBirthDate';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const MIN_YEAR = 1900;

function currentYear(): number {
  return new Date().getFullYear();
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function isoFromParts(year: number, month: number, day: number): string {
  const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return normalizeDateOfBirth(iso) ?? '';
}

function partsFromIso(iso?: string): { year: number; month: number; day: number } | null {
  const norm = normalizeDateOfBirth(iso);
  if (!norm) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(norm);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export type BookingDobPickerModalProps = {
  open: boolean;
  valueIso: string;
  onClose: () => void;
  onConfirm: (iso: string) => void;
};

type Step = 'year' | 'month' | 'day';

export default function BookingDobPickerModal({ open, valueIso, onClose, onConfirm }: BookingDobPickerModalProps) {
  const initial = partsFromIso(valueIso);
  const [step, setStep] = useState<Step>('year');
  const [year, setYear] = useState(initial?.year ?? 1980);
  const [month, setMonth] = useState(initial?.month ?? 1);
  const [day, setDay] = useState(initial?.day ?? 1);
  const yearListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const p = partsFromIso(valueIso);
    setStep('year');
    setYear(p?.year ?? 1980);
    setMonth(p?.month ?? 1);
    setDay(p?.day ?? 1);
  }, [open, valueIso]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || step !== 'year' || !yearListRef.current) return;
    const el = yearListRef.current.querySelector(`[data-year="${year}"]`);
    el?.scrollIntoView({ block: 'center' });
  }, [open, step, year]);

  const years = useMemo(() => {
    const end = currentYear();
    const list: number[] = [];
    for (let y = MIN_YEAR; y <= end; y += 1) list.push(y);
    return list;
  }, []);

  const maxDay = daysInMonth(year, month);
  const safeDay = Math.min(day, maxDay);

  const subtitle =
    step === 'year'
      ? 'Please scroll and select the year'
      : step === 'month'
        ? 'Please select the month'
        : 'Please select the day';

  const handleOk = () => {
    const iso = isoFromParts(year, month, safeDay);
    if (iso) onConfirm(iso);
    onClose();
  };

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open) return null;

  const content = (
    <div className="booking-dob-modal-root" role="presentation" onClick={handleBackdrop}>
      <div className="booking-dob-modal" role="dialog" aria-modal="true" aria-labelledby="booking-dob-title">
        <div className="booking-dob-modal__header">
          <h4 id="booking-dob-title" className="booking-dob-modal__date-line">
            <span className={step === 'day' ? 'is-active' : ''}>{String(safeDay).padStart(2, '0')}</span>{' '}
            <span className={step === 'month' ? 'is-active' : ''}>{MONTH_LABELS[month - 1]}</span>,{' '}
            <span className={step === 'year' ? 'is-active' : ''}>{year}</span>
          </h4>
          <h6 className="booking-dob-modal__hint">{subtitle}</h6>
        </div>

        <div className="booking-dob-modal__body">
          {step === 'year' && (
            <div className="booking-dob-modal__year-list" ref={yearListRef}>
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  data-year={y}
                  className={`booking-dob-modal__year-item${y === year ? ' is-selected' : ''}`}
                  onClick={() => {
                    setYear(y);
                    setStep('month');
                  }}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
          {step === 'month' && (
            <div className="booking-dob-modal__month-grid">
              {MONTH_LABELS.map((label, i) => {
                const m = i + 1;
                return (
                  <button
                    key={label}
                    type="button"
                    className={`booking-dob-modal__month-item${m === month ? ' is-selected' : ''}`}
                    onClick={() => {
                      setMonth(m);
                      setDay((d) => Math.min(d, daysInMonth(year, m)));
                      setStep('day');
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {step === 'day' && (
            <div className="booking-dob-modal__day-grid">
              {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`booking-dob-modal__day-item${d === safeDay ? ' is-selected' : ''}`}
                  onClick={() => setDay(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="booking-dob-modal__actions">
          <button type="button" className="booking-dob-modal__btn booking-dob-modal__btn--cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="booking-dob-modal__btn booking-dob-modal__btn--ok"
            onClick={handleOk}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
